import { uiLogger } from "../utils/logger";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AdminDocsPage(): JSX.Element {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);

  useEffect(() => {
    // Check if docs are available
    const checkDocs = async () => {
      try {
        const response = await fetch('/docs/index.html');
        if (!response.ok) {
          throw new Error('Documentation not found');
        }
        setIsLoading(false);
      } catch (error) {
        uiLogger.error('Error loading documentation:', error);
        setDocsError('Documentation is not available. Please ensure the docs have been deployed to the /docs directory.');
        setIsLoading(false);
      }
    };

    checkDocs();
  }, []);

  const handleBackToHome = (): void => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading documentation...</p>
        </div>
      </div>
    );
  }

  if (docsError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm">
          <div className="container-max section-padding">
            <div className="flex items-center justify-between py-3">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Documentation
              </h1>
              <button
                onClick={handleBackToHome}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
              >
                Home
              </button>
            </div>
          </div>
        </header>

        {/* Error Content */}
        <main className="container-max section-padding py-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
                </div>
                <div className="ml-4">
                  <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                    Documentation Not Available
                  </h2>
                  <p className="text-red-700 dark:text-red-300 mb-4">
                    {docsError}
                  </p>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                      To deploy your documentation:
                    </h3>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <li>Generate your documentation files</li>
                      <li>Upload the files to your S3 bucket under the <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/docs</code> path</li>
                      <li>Ensure the main entry point is <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/docs/index.html</code></li>
                      <li>Refresh this page to access the documentation</li>
                    </ol>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleBackToHome}
                      className="btn-primary"
                    >
                      Back to Home
                    </button>
                    <button
                      onClick={() => window.location.reload()}
                      className="btn-secondary"
                    >
                      Retry Loading Docs
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container-max section-padding">
          <div className="flex items-center justify-between py-3">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              Documentation
            </h1>
            <button
              onClick={handleBackToHome}
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
            >
              Home
            </button>
          </div>
        </div>
      </header>

      {/* Documentation Content */}
      <main className="h-screen">
        <iframe
          src="/docs/index.html"
          className="w-full h-full border-0"
          title="Fabrik 28 Documentation"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </main>
    </div>
  );
}
